import { useMutation } from '@tanstack/react-query';
import { Check, Mail, Send } from 'lucide-react';
import { Button } from '../../../components/ui';
import { ApiError, api } from '../../../lib/api';

/**
 * "Send top-up link" — ONE click, from the screen that raised the alarm.
 *
 * The loop this closes was the worst step-count in the product: `/support` and
 * this tab would tell you a client runs out of hours on the 29th, and the only
 * way to act was Settings → Client portal → pick the client from a list →
 * Create link → copy → open a mail client. Four navigations and a retype, to
 * act on an alert the app had just raised — worse than the spreadsheet plus an
 * email it replaced.
 *
 * It adds no field: the address is the client's own billing email, and the
 * server mints the token, supersedes any live link for that client and mails
 * it in one call (`POST /api/support/portal/send-link`). There is exactly one
 * implementation of that — this is a second CALLER, not a second copy.
 *
 * Honesty rules it inherits from the invoice composer and the timesheet
 * reminder: `filed` is never dressed up as `sent`, and a refusal is printed in
 * the server's own words (a client with no billing email is told exactly that,
 * and Edit is one click away).
 */
export function SendTopUpLink({
  clientId,
  clientName,
  size = 'sm',
}: {
  clientId: string;
  clientName: string;
  size?: 'sm' | 'md';
}) {
  const send = useMutation({
    mutationFn: () =>
      api.post<{ outcome: string; to: string; expiresAt: string }>('/support/portal/send-link', {
        clientId,
      }),
  });
  const result = send.data;

  return (
    <>
      <Button
        variant="outline"
        size={size}
        onClick={() => send.mutate()}
        disabled={send.isPending}
        title={`Mail ${clientName} a signed link to their support-hours page, where they can see the balance and buy more. It goes to the billing email on this client and supersedes any link already out.`}
      >
        {result ? (
          <Check className="h-4 w-4" aria-hidden />
        ) : (
          <Send className="h-4 w-4" aria-hidden />
        )}
        {send.isPending ? 'Sending…' : result ? 'Link sent' : 'Send top-up link'}
      </Button>

      {result && (
        <p className="flex w-full items-start gap-1.5 text-caption text-grey-500">
          <Mail className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>
            {result.outcome === 'sent' ? (
              <>
                Sent to <span className="font-semibold text-secondary">{result.to}</span>.
              </>
            ) : result.outcome === 'filed' ? (
              <>
                <span className="font-semibold text-warning">Not transmitted</span> —
                EMAIL_MODE=file, so the message for{' '}
                <span className="font-semibold text-secondary">{result.to}</span> was written to{' '}
                <code>.mail/</code>.
              </>
            ) : (
              <>
                {result.outcome} → {result.to}
              </>
            )}{' '}
            The link supersedes any earlier one and expires{' '}
            {new Date(result.expiresAt).toLocaleDateString()}.
          </span>
        </p>
      )}

      {send.isError && (
        <p className="w-full text-caption font-semibold text-status-late">
          {send.error instanceof ApiError
            ? send.error.message
            : 'Could not send the link — try again.'}
        </p>
      )}
    </>
  );
}
